﻿import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useBookDemo } from './BookDemoContext'
import './Navbar.css'
import PossoBuildLogo from './PossoBuildLogo'
import SmartHireOrgSignInModal from './SmartHireOrgSignInModal'
import SmartHireCandidateSignInModal from './SmartHireCandidateSignInModal'
import { checkLogin, sessionLogout, ORG_DASHBOARD, CANDIDATE_DASHBOARD } from './SmartHireAuth'

const SMARTHIRE_BASE = 'https://possobuild.ai'

const SIGN_IN_LINKS = [
  { label: 'SmartHire', desc: 'AI Interviewing Platform', icon: '🎯', modal: 'org' },
  { label: 'SmartDevelop', desc: 'Career Readiness AI', icon: '📈', modal: 'candidate' },
]

const hasPicture = (pic: string | undefined) => pic && !pic.startsWith('https://graph.microsoft.com')

const getInitials = (name: string) => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0][0].toUpperCase()
}

const getDashboardUrl = (userType: string) => {
  if (userType === 'orgUser') return ORG_DASHBOARD
  if (userType === 'aspUser') return CANDIDATE_DASHBOARD
  return SMARTHIRE_BASE
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [signOpen, setSignOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null)
  const [activeModal, setActiveModal] = useState<string | null>(null)
  const [user, setUser] = useState<{ name: string; email: string; picture?: string; userType: string } | null>(null)
  const dropRef = useRef<HTMLDivElement>(null)
  const openBookDemo = useBookDemo()

  const openModal = (modalKey: string) => {
    setSignOpen(false)
    setExpandedProduct(null)
    setMobileOpen(false)
    setActiveModal(modalKey)
  }

  // Check if user is already signed in (shared session cookie)
  useEffect(() => {
    checkLogin().then(u => setUser(u))
  }, [])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Close sign dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setSignOpen(false)
        setExpandedProduct(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const navigate = useNavigate()
  const location = useLocation()

  const scrollTo = (id: string) => {
    setMobileOpen(false)
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } })
      return
    }
    const el = document.getElementById(id)
    if (el) {
      // Push a history entry so Back returns to current scroll position
      window.history.pushState({ scrollY: window.scrollY }, '', `#${id}`)
      const offset = 72
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' })
    }
  }

  // Restore scroll position on Back button
  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      if (e.state?.scrollY != null) {
        window.scrollTo({ top: e.state.scrollY, behavior: 'smooth' })
      }
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  // Handle scroll after navigating home from another page
  useEffect(() => {
    if (location.pathname === '/' && location.state?.scrollTo) {
      const id = location.state.scrollTo
      window.history.replaceState({}, '')
      setTimeout(() => {
        const el = document.getElementById(id)
        if (el) {
          const offset = 72
          window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' })
        }
      }, 100)
    }
  }, [location])

  return (
    <nav className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}>
      <div className="container">
        <div className="nav-content">
          {/* Logo */}
          <a href="/" className="nav-logo" aria-label="PossoBuild home">
            <PossoBuildLogo />
          </a>

          {/* Desktop links */}
          <ul className="nav-links">
            <li><button className="nav-link-btn" onClick={() => scrollTo('products')}>Products</button></li>
            <li><button className="nav-link-btn" onClick={() => scrollTo('platform')}>Platform</button></li>
            <li><button className="nav-link-btn" onClick={() => scrollTo('industries')}>Industries</button></li>
            <li><button className="nav-link-btn" onClick={() => scrollTo('about')}>About</button></li>
            <li><Link to="/product-tour" className="nav-link-btn" onClick={() => setMobileOpen(false)}>Product Tour</Link></li>
            <li><Link to="/how-it-works" className="nav-link-btn" onClick={() => setMobileOpen(false)}>How It Works</Link></li>
          </ul>

          {/* Right side actions */}
          <div className="nav-actions">
            <button className="btn-primary nav-demo-btn" onClick={() => openBookDemo('Book a Demo')}>Book a Demo</button>
            {user ? (
              /* ── SIGNED-IN STATE ── */
              <div className="sign-drop" ref={dropRef}>
                <button
                  className={`user-toggle${signOpen ? ' user-toggle--open' : ''}`}
                  onClick={() => setSignOpen(v => !v)}
                  aria-haspopup="true"
                  aria-expanded={signOpen}
                >
                  {hasPicture(user.picture) ? (
                    <img className="user-toggle__avatar" src={user.picture} alt="" />
                  ) : (
                    <span className="user-toggle__initials">{getInitials(user.name || user.email)}</span>
                  )}
                  <span className="user-toggle__name">{user.name || user.email}</span>
                  <svg className="sign-drop__chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {signOpen && (
                  <div className="sign-drop__menu" role="menu">
                    <div className="sign-drop__header">Go to</div>
                    <a href={getDashboardUrl(user.userType)} className="sign-drop__item" role="menuitem" onClick={() => setSignOpen(false)}>
                      <span className="sign-drop__item-icon">{user.userType === 'orgUser' ? '🎯' : '📈'}</span>
                      <span className="sign-drop__item-body">
                        <span className="sign-drop__item-label">{user.userType === 'orgUser' ? 'SmartHire' : 'SmartDevelop'}</span>
                        <span className="sign-drop__item-desc">{user.userType === 'orgUser' ? 'AI Interviewing Platform' : 'Career Readiness AI'}</span>
                      </span>
                      <svg className="sign-drop__item-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </a>
                    <button
                      className="sign-drop__item sign-drop__item--signout"
                      role="menuitem"
                      onClick={async () => {
                        setSignOpen(false)
                        await sessionLogout()
                        setUser(null)
                      }}
                    >
                      <span className="sign-drop__item-icon">🚪</span>
                      <span className="sign-drop__item-body">
                        <span className="sign-drop__item-label">Sign Out</span>
                      </span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* ── NOT SIGNED-IN STATE ── */
              <div className="sign-drop" ref={dropRef}>
                <button
                  className={`sign-drop__trigger${signOpen ? ' sign-drop__trigger--open' : ''}`}
                  onClick={() => setSignOpen(v => !v)}
                  aria-haspopup="true"
                  aria-expanded={signOpen}
                >
                  Sign In
                  <svg className="sign-drop__chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {signOpen && (
                  <div className="sign-drop__menu" role="menu">
                    <div className="sign-drop__header">Sign in to a product</div>
                    {SIGN_IN_LINKS.map(item => (
                      <button
                        key={item.label}
                        className="sign-drop__item"
                        role="menuitem"
                        onClick={() => openModal(item.modal)}
                      >
                        <span className="sign-drop__item-icon">{item.icon}</span>
                        <span className="sign-drop__item-body">
                          <span className="sign-drop__item-label">{item.label}</span>
                          <span className="sign-drop__item-desc">{item.desc}</span>
                        </span>
                        <svg className="sign-drop__item-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className={`mobile-menu-btn${mobileOpen ? ' mobile-menu-btn--open' : ''}`}
            onClick={() => setMobileOpen(v => !v)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="mobile-menu">
            <button className="nav-link-btn" onClick={() => scrollTo('products')}>Products</button>
            <button className="nav-link-btn" onClick={() => scrollTo('platform')}>Platform</button>
            <button className="nav-link-btn" onClick={() => scrollTo('industries')}>Industries</button>
            <button className="nav-link-btn" onClick={() => scrollTo('about')}>About</button>
            <Link to="/product-tour" className="nav-link-btn" onClick={() => setMobileOpen(false)}>Product Tour</Link>
            <Link to="/how-it-works" className="nav-link-btn" onClick={() => setMobileOpen(false)}>How It Works</Link>
            <div className="mobile-menu__divider" />
            {user ? (
              <>
                <div className="mobile-menu__user-info">
                  {hasPicture(user.picture) ? (
                    <img className="user-toggle__avatar" src={user.picture} alt="" />
                  ) : (
                    <span className="user-toggle__initials">{getInitials(user.name || user.email)}</span>
                  )}
                  <span className="mobile-menu__user-name">{user.name || user.email}</span>
                </div>
                <a href={getDashboardUrl(user.userType)} className="mobile-menu__sign-link">
                  {user.userType === 'orgUser' ? '🎯 SmartHire' : '📈 SmartDevelop'}
                </a>
                <button
                  className="mobile-menu__sign-link"
                  onClick={async () => { setMobileOpen(false); await sessionLogout(); setUser(null) }}
                >
                  🚪 Sign Out
                </button>
              </>
            ) : (
              <>
                <p className="mobile-menu__sign-label">Sign in to:</p>
                {SIGN_IN_LINKS.map(item => (
                  <button key={item.label} className="mobile-menu__sign-link" onClick={() => openModal(item.modal)}>
                    {item.icon} {item.label}
                  </button>
                ))}
              </>
            )}
            <button className="btn-primary" style={{marginTop:'1rem',width:'100%'}} onClick={() => openBookDemo('Book a Demo')}>Book a Demo</button>
          </div>
        )}
      </div>
      {activeModal === 'org' && <SmartHireOrgSignInModal onClose={() => setActiveModal(null)} />}
      {activeModal === 'candidate' && <SmartHireCandidateSignInModal onClose={() => setActiveModal(null)} />}
    </nav>
  )
}
